import { Box, Grid, Switch, TextField, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { AssignmentTurnedIn, Autorenew, CalendarToday, EventNote, LocationOnRounded, People } from '@material-ui/icons';
import { useFormikContext } from 'formik';
import React from 'react';
import { DateTime } from 'luxon';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { ActivityFormValues } from '../ActivityFormV2';

const toggleStyle = (active: boolean) =>
  active ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 14px' } : { padding: '6px 14px' };

const ToggleLabel = ({ children }: { children: string }) => (
  <Typography style={{ fontWeight: 600, fontSize: 11 }}>{children}</Typography>
);

const OptionRow = ({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) => (
  <Box mb={3}>
    <Typography style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 2 }}>{label}</Typography>
    <Typography style={{ fontSize: 12, color: '#808080', marginBottom: 8 }}>{description}</Typography>
    {children}
  </Box>
);

const FooterItem = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <Box display='flex' alignItems='center' mr={2}>
    {icon}
    <Typography variant='caption' style={{ color: '#999', marginLeft: 4 }}>
      {text}
    </Typography>
  </Box>
);

export const AdvancedStep = () => {
  const { values, setFieldValue } = useFormikContext<ActivityFormValues>();
  const { theme } = useCampfireTheme();

  const frequencyLabel = (() => {
    if (!values.frequency) return 'Not set';
    if (values.frequency === 'no-repeat') return 'Once off';
    if (values.frequency === 'custom') return 'Custom recurrence';
    return values.frequency.charAt(0).toUpperCase() + values.frequency.slice(1).replace(/-/g, ' ');
  })();

  const locationText = (() => {
    if (values.locationType === 'remote') return values.remoteLocation?.details || 'Remote';
    if (values.location) return values.location.description;
    return 'No location set';
  })();

  const dateText = (() => {
    if (!values.startDate) return 'No date set';
    const start = DateTime.fromJSDate(values.startDate).toFormat('dd MMM yyyy');
    if (values.endDate) {
      return `${start} – ${DateTime.fromJSDate(values.endDate).toFormat('dd MMM yyyy')}`;
    }
    return start;
  })();

  const teamCount = values.invitedVolunteers.length;
  const alCount = values.invitedVolunteers.filter((v) => v.isActivityLeader).length;

  const isSelfDirected = values.activityType === 'self-directed';
  const isEvent = values.activityType === 'event';
  const sessionLabel = isSelfDirected ? 'action' : 'session';

  const TypeIcon = isSelfDirected ? AssignmentTurnedIn : isEvent ? EventNote : Autorenew;
  const typeLabel = isSelfDirected ? 'Self-Directed' : isEvent ? 'Event' : frequencyLabel;

  return (
    <Box>
      <Typography style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Review &amp; Create</Typography>
      <Typography variant='body2' color='textSecondary' style={{ marginBottom: 24 }}>
        {`Review your activity before creating. You can go back to any step to make changes.`}
      </Typography>

      <Box display='flex' borderRadius={8} border={`1px solid ${theme.color.grey.border}`} overflow='hidden' mb={3}>
        <Box minWidth={36} width={36} bgcolor={theme.palette.primary.main} borderRadius='8px 0 0 8px' />
        <Box flex={1} bgcolor={theme.color.white.neutral50} p='20px 24px'>
          <Typography
            variant='h6'
            style={{ fontWeight: 800, color: theme.color.grey.neutral400, letterSpacing: '-0.02em' }}
          >
            {values.activityName || 'Untitled Activity'}
          </Typography>

          <Box display='flex' flexWrap='wrap' mt={2.5}>
            {!isSelfDirected && (
              <FooterItem
                icon={<CalendarToday style={{ color: theme.color.grey.neutral200, fontSize: 14 }} />}
                text={dateText}
              />
            )}
            <FooterItem
              icon={<TypeIcon style={{ color: theme.color.grey.neutral200, fontSize: 14 }} />}
              text={typeLabel}
            />
            {!isSelfDirected && (
              <FooterItem
                icon={<LocationOnRounded style={{ color: theme.color.grey.neutral200, fontSize: 14 }} />}
                text={locationText}
              />
            )}
            <FooterItem
              icon={
                <Typography
                  style={{ fontWeight: 600, color: theme.color.grey.neutral200, fontSize: 14, marginRight: -2 }}
                >
                  {values.sessions.length}
                </Typography>
              }
              text={`${sessionLabel}${values.sessions.length !== 1 ? 's' : ''} available`}
            />
            <FooterItem
              icon={<People style={{ color: theme.color.grey.neutral200, fontSize: 14 }} />}
              text={teamCount === 0 ? 'No team members' : `${teamCount} added${alCount > 0 ? ` (${alCount} AL)` : ''}`}
            />
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={6} sm={3}>
          <Typography
            style={{
              fontSize: 11,
              color: '#aaa',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Joining
          </Typography>
          <Typography style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>
            {values.isRestrictedActivity ? 'Requires approval' : 'Open'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography
            style={{
              fontSize: 11,
              color: '#aaa',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Roster type
          </Typography>
          <Typography style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>
            {values.hasOpenRoster ? 'Open' : 'Managed'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography
            style={{
              fontSize: 11,
              color: '#aaa',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Hidden
          </Typography>
          <Typography style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>
            {values.isHidden ? 'Yes' : 'No'}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography
            style={{
              fontSize: 11,
              color: '#aaa',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Max team
          </Typography>
          <Typography style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>
            {values.maxTeam ?? 'No limit'}
          </Typography>
        </Grid>
      </Grid>

      <Box mt={6} mb={2} bgcolor='#F9F9F9' borderRadius={8} border='1px solid #EFEFEF' p={3}>
        <Typography style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Advanced Settings</Typography>
        <Typography variant='body2' color='textSecondary' style={{ marginBottom: 20 }}>
          Optional configuration for event mode, roster behaviour, and address visibility.
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <OptionRow label='Roster Comments' description='Control whether volunteers can comment on rosters'>
              <ToggleButtonGroup
                value={String(values.allowRosterComments)}
                exclusive
                onChange={(_unusedEvent, val) => {
                  if (val === null) return;
                  if (val === 'true') setFieldValue('allowRosterComments', true);
                  else if (val === 'false') setFieldValue('allowRosterComments', false);
                  else setFieldValue('allowRosterComments', null);
                }}
              >
                <ToggleButton value='true' style={toggleStyle(values.allowRosterComments === true)}>
                  <ToggleLabel>Allow</ToggleLabel>
                </ToggleButton>
                <ToggleButton value='null' style={toggleStyle(values.allowRosterComments === null)}>
                  <ToggleLabel>Inherit</ToggleLabel>
                </ToggleButton>
                <ToggleButton value='false' style={toggleStyle(values.allowRosterComments === false)}>
                  <ToggleLabel>Prevent</ToggleLabel>
                </ToggleButton>
              </ToggleButtonGroup>
            </OptionRow>

            <OptionRow
              label='Check In / Check Out'
              description='Control whether volunteers can check in and out of sessions'
            >
              <ToggleButtonGroup
                value={String(values.allowCICO)}
                exclusive
                onChange={(_unusedEvent, val) => {
                  if (val === null) return;
                  if (val === 'true') setFieldValue('allowCICO', true);
                  else if (val === 'false') setFieldValue('allowCICO', false);
                  else setFieldValue('allowCICO', null);
                }}
              >
                <ToggleButton value='true' style={toggleStyle(values.allowCICO === true)}>
                  <ToggleLabel>Allow</ToggleLabel>
                </ToggleButton>
                <ToggleButton value='null' style={toggleStyle(values.allowCICO === null)}>
                  <ToggleLabel>Inherit</ToggleLabel>
                </ToggleButton>
                <ToggleButton value='false' style={toggleStyle(values.allowCICO === false)}>
                  <ToggleLabel>Prevent</ToggleLabel>
                </ToggleButton>
              </ToggleButtonGroup>
            </OptionRow>
          </Grid>

          <Grid item xs={12} sm={6}>
            <OptionRow
              label='Individual Shift Times'
              description='Allow setting individual start and end times per volunteer on a roster'
            >
              <ToggleButtonGroup
                value={values.allowIndividualShiftTimes}
                exclusive
                onChange={(_unusedEvent, newValue) => {
                  if (newValue !== undefined) setFieldValue('allowIndividualShiftTimes', newValue);
                }}
              >
                <ToggleButton value={false} style={toggleStyle(values.allowIndividualShiftTimes === false)}>
                  <ToggleLabel>No</ToggleLabel>
                </ToggleButton>
                <ToggleButton value={null} style={toggleStyle(values.allowIndividualShiftTimes === null)}>
                  <ToggleLabel>Inherit</ToggleLabel>
                </ToggleButton>
                <ToggleButton value style={toggleStyle(values.allowIndividualShiftTimes === true)}>
                  <ToggleLabel>Yes</ToggleLabel>
                </ToggleButton>
              </ToggleButtonGroup>
            </OptionRow>

            <OptionRow
              label='Flexible Roster Approval'
              description='Require approval before ad-hoc rosters are visible to volunteers'
            >
              <ToggleButtonGroup
                value={values.requireRosterTemplateApproval}
                exclusive
                onChange={(_unusedEvent, newValue) => {
                  if (newValue !== null) setFieldValue('requireRosterTemplateApproval', newValue);
                }}
              >
                <ToggleButton value={false} style={toggleStyle(values.requireRosterTemplateApproval === false)}>
                  <ToggleLabel>No</ToggleLabel>
                </ToggleButton>
                <ToggleButton value style={toggleStyle(values.requireRosterTemplateApproval === true)}>
                  <ToggleLabel>Yes</ToggleLabel>
                </ToggleButton>
              </ToggleButtonGroup>
            </OptionRow>
          </Grid>

          <Grid item xs={12} sm={6}>
            <OptionRow
              label='Event Mode'
              description='Streamlines how volunteers join and get rostered for this activity.'
            >
              <Box display='flex' alignItems='center' mb={1}>
                <Switch
                  size='small'
                  color='primary'
                  checked={values.isEvent}
                  onChange={(e) => setFieldValue('isEvent', e.target.checked)}
                />
                <Typography style={{ fontSize: 13, fontWeight: 500, color: '#333', marginLeft: 8 }}>
                  {values.isEvent ? 'Enabled' : 'Disabled'}
                </Typography>
              </Box>
              {values.isEvent && (
                <Box pl={1} mt={1} borderLeft='2px solid #e0e0e0'>
                  <Typography style={{ fontSize: 12, color: '#666', lineHeight: 1.7 }}>
                    <strong>Single session, single date:</strong> Volunteers are automatically{' '}
                    {values.hasOpenRoster ? 'rostered' : 'marked as available'} when{' '}
                    {values.isRestrictedActivity ? 'approved by a manager' : 'they join'}.
                  </Typography>
                  <Typography style={{ fontSize: 12, color: '#666', lineHeight: 1.7, marginTop: 4 }}>
                    <strong>Multiple sessions or days:</strong>{' '}
                    {values.isRestrictedActivity ? 'After approval, volunteers' : 'Volunteers'} choose which sessions
                    and days they want to attend.{' '}
                    {values.hasOpenRoster
                      ? 'They are rostered into their chosen sessions immediately.'
                      : 'Their preferences are recorded as availability for the manager to finalise.'}
                  </Typography>
                  <Typography style={{ fontSize: 11, color: '#999', lineHeight: 1.6, marginTop: 8 }}>
                    Change whether volunteers are rostered or marked as available via <strong>Roster type</strong>{' '}
                    (Privacy &amp; Team step). Change whether approval is required via <strong>Joining policy</strong>.
                  </Typography>
                </Box>
              )}
            </OptionRow>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 2 }}>
              Address Hiding
            </Typography>
            <Typography style={{ fontSize: 12, color: '#808080', marginBottom: 12 }}>
              Control how and when the address is visible to volunteers for this activity
            </Typography>

            <Box display='flex' alignItems='center' mb={1}>
              <Switch
                size='small'
                color='primary'
                checked={values.addressHidingExempt ?? false}
                onChange={(event) => setFieldValue('addressHidingExempt', event.target.checked)}
              />
              <Box ml={1}>
                <Typography style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>
                  Exempt from address hiding
                </Typography>
                <Typography style={{ fontSize: 12, color: '#808080' }}>
                  When enabled, the address is always visible regardless of organisation-wide hiding settings
                </Typography>
              </Box>
            </Box>

            {!values.addressHidingExempt && (
              <Box mt={2} pl={6}>
                <Typography style={{ fontWeight: 500, fontSize: 13, color: '#333', marginBottom: 2 }}>
                  Time window override
                </Typography>
                <Typography style={{ fontSize: 12, color: '#808080', marginBottom: 12 }}>
                  Override the organisation-wide address hiding time windows. Leave empty to use the org default.
                </Typography>
                <Box display='flex'>
                  <TextField
                    type='number'
                    size='small'
                    variant='outlined'
                    onChange={(e) =>
                      setFieldValue(
                        'addressRevealBeforeDays',
                        e.target.value === '' ? null : parseInt(e.target.value, 10)
                      )
                    }
                    value={values.addressRevealBeforeDays ?? ''}
                    label='Reveal before (days)'
                    inputProps={{ step: '1', min: '1', max: '30' }}
                    style={{ width: 180, marginRight: 16 }}
                  />
                  <TextField
                    type='number'
                    size='small'
                    variant='outlined'
                    onChange={(e) =>
                      setFieldValue('addressHideAfterDays', e.target.value === '' ? null : parseInt(e.target.value, 10))
                    }
                    value={values.addressHideAfterDays ?? ''}
                    label='Hide after (days)'
                    inputProps={{ step: '1', min: '1', max: '30' }}
                    style={{ width: 180 }}
                  />
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
